// @arch:pure-engine
// planContextDigest.js — RCA lentidão fix G: mata o COLD-START das tasks do DAG.
//
// O maior ofensor medido do "40+ min na primeira task": o worker de cada task nasce
// com SÓ doctrine + texto da task (dagWorkerPrompt), ZERO contexto da fase de plano.
// O planner acabou de ler o codebase, decidir abordagem e escrever o spec — e a
// primeira task jogava tudo fora e re-lia do zero (10-25 iterações de read/grep a
// ~32s cada ANTES do primeiro edit).
//
// Aqui destilamos as mensagens do feed da sessão num DIGEST determinístico (sem LLM:
// barato, testável, zero latência — mesma filosofia do convoDigest) com os fatos
// operacionais que o worker precisa pra COMEÇAR EDITANDO: arquivos já lidos/editados,
// buscas feitas, comandos rodados e o resumo do plano aprovado. Vai como bloco SYSTEM
// estável (idêntico em todas as tasks do run ⇒ prompt-cache friendly).

const clip = (s, n) => { const t = String(s || '').replace(/\s+/g, ' ').trim(); return t.length > n ? `${t.slice(0, n - 1)}…` : t; };

const READ_TOOLS = new Set(['read_file', 'list_files']);
const SEARCH_TOOLS = new Set(['grep_search', 'semantic_search']);
const EDIT_TOOLS = new Set(['write_file', 'apply_edit']);

const CAPS = { reads: 25, searches: 10, edits: 12, commands: 8, planChars: 1200, totalChars: 4000 };

function pushUnique(list, v, cap) {
  if (!v || list.includes(v)) return;
  if (list.length < cap) list.push(v);
}

const strArg = (args, ...keys) => {
  for (const k of keys) { if (typeof args[k] === 'string' && args[k]) return args[k]; }
  return '';
};

/** Fold de UM tool card do feed ({tool_name, tool_args}) nos acumuladores. */
function foldToolCard(name, args, acc) {
  const path = strArg(args, 'path', 'filePath');
  if (READ_TOOLS.has(name) && path) pushUnique(acc.reads, path, CAPS.reads);
  if (SEARCH_TOOLS.has(name)) {
    const q = strArg(args, 'query', 'pattern');
    if (q) pushUnique(acc.searches, clip(q, 60), CAPS.searches);
  }
  if (EDIT_TOOLS.has(name) && path) pushUnique(acc.edits, path, CAPS.edits);
  if (name === 'run_command' && strArg(args, 'command')) {
    pushUnique(acc.commands, clip(args.command, 90), CAPS.commands);
  }
}

/**
 * Fold de UMA mensagem do feed (shape do renderer: {role, content, tool_name,
 * tool_args}) nos acumuladores. Mensagens malformadas são ignoradas.
 */
function foldFeedMessage(m, acc) {
  if (!m || typeof m !== 'object') return;
  if (m.role === 'tool') {
    const args = (m.tool_args && typeof m.tool_args === 'object') ? m.tool_args : {};
    foldToolCard(m.tool_name || '', args, acc);
    return;
  }
  // Resumo do plano: a ÚLTIMA mensagem substantiva do agente (a apresentação/raciocínio
  // do plano). Curtas (ack, "ok") não servem de resumo.
  if (m.role === 'agent' && typeof m.content === 'string' && m.content.trim().length >= 80) {
    acc.planSummary = clip(m.content, CAPS.planChars);
  }
}

/**
 * Destila as mensagens do feed (fase de plano incluída) num digest capado pro worker
 * de cada task do DAG. '' quando não há NADA a aproveitar (⇒ prompt byte-idêntico ao
 * de antes do fix G — o caller pula o bloco).
 *
 * @param {Array<object>} messages  mensagens do feed da sessão (renderer shape)
 * @param {{ windowSize?: number }} [opts]
 * @returns {string}
 */
export function buildPlanContextDigest(messages, { windowSize = 100 } = {}) {
  const list = Array.isArray(messages) ? messages : [];
  const win = list.slice(-Math.max(1, windowSize));
  const acc = { reads: [], searches: [], edits: [], commands: [], planSummary: '' };
  for (const m of win) foldFeedMessage(m, acc);

  const lines = [];
  if (acc.reads.length) lines.push(`Arquivos JÁ LIDOS no plano (comece por eles; releia só o que for editar): ${acc.reads.join(', ')}`);
  if (acc.searches.length) lines.push(`Buscas já feitas: ${acc.searches.join(' · ')}`);
  if (acc.edits.length) lines.push(`Arquivos já criados/editados: ${acc.edits.join(', ')}`);
  if (acc.commands.length) lines.push(`Comandos já rodados: ${acc.commands.join(' · ')}`);
  if (acc.planSummary) lines.push(`Resumo do plano aprovado:\n${acc.planSummary}`);
  // (review adversarial) spec NÃO entra aqui: no wiring vivo do DAG spec é sempre null
  // (o approve não o repassa) e o acceptance já chega POR TASK no corpo do worker.

  if (!lines.length) return '';
  const head = 'CONTEXTO DA FASE DE PLANO (o planner já levantou isto — USE em vez de re-descobrir do zero; vá DIRETO ao trabalho da task):';
  const out = [head, ...lines].join('\n');
  return out.length > CAPS.totalChars ? `${out.slice(0, CAPS.totalChars - 1)}…` : out;
}

export const _internals = { foldFeedMessage, CAPS };
